import React, { useState } from 'react';
import { useIdentity } from './IdentityContext';
import { useConfirm } from './ConfirmContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { ImageThumb, UploadButton, fileToBase64, validateFiles } from './ImageUploadControls';
import { categoryImageUrl } from '../data/categoryImageUrl';
import { GOLD_LT } from '../theme';

export default function CoverImageManager({ category, onChange }) {
  const { authFetch } = useIdentity();
  const confirm = useConfirm();
  const toast = useToast();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(false);

  const cover = category.coverImage;

  const applyDoc = (doc) => {
    const updated = doc.categories.find((c) => c.slug === category.slug);
    if (updated) onChange(updated);
  };

  const upload = async (files) => {
    const [file] = validateFiles(files, setError);
    setError(null);
    if (!file) return;

    setUploading(true);
    try {
      const dataBase64 = await fileToBase64(file);
      const res = await authFetch('/.netlify/functions/categories-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categorySlug: category.slug, kind: 'cover', contentType: file.type, dataBase64 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      applyDoc(data.doc);
      toast.success('Cover image uploaded.');
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!cover) return;
    const ok = await confirm('Remove the cover image from the draft?');
    if (!ok) return;
    setRemoving(true);
    try {
      const res = await authFetch('/.netlify/functions/categories-images', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categorySlug: category.slug, imageId: cover.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove image');
      applyDoc(data.doc);
      toast.success('Cover image removed.');
    } catch (err) {
      toast.error(err.message || 'Failed to remove image.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: GOLD_LT, marginBottom: 8 }}>Cover Image</div>
      {error && <p style={{ color: '#e08a8a', fontSize: 13 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        {cover && <ImageThumb image={cover} size={80} urlFn={categoryImageUrl} />}
        <UploadButton label={cover ? 'Replace' : 'Upload'} uploading={uploading} onUpload={upload} />
      </div>
      {cover && (
        <button onClick={remove} disabled={removing} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: '#e08a8a', background: 'none', border: 'none', cursor: removing ? 'default' : 'pointer', opacity: removing ? .6 : 1, padding: 0 }}>
          {removing && <Spinner size={11} />}
          {removing ? 'Removing…' : 'Remove'}
        </button>
      )}
    </div>
  );
}
